import type { Order, OrderItem } from '@/services/orderService';
import { ref, type Ref } from 'vue';
import orderService from '@/services/orderService';
import { 
  getKitchenVisibleItems, 
  areAllItemsPreparing, 
  areAllItemsReady,
  initializeOrderItems
} from '@/utils/kitchenHelpers';

/**
 * Composable for kitchen item actions
 * Implements Single Responsibility Principle - only handles item status updates
 */
export function useKitchenItems(activeOrders: Ref<Order[]>) {
  /**
   * Mark individual item as preparing
   */
  const markItemPreparing = async (order: Order, item: OrderItem) => {
    try {
      // Update the item status
      await orderService.updateOrderItemStatus(order.id, item.id, 'preparing');

      // Fetch the updated order to get current state of all items
      const updatedOrder = await orderService.getOrder(order.id);

      // Check if all items are now preparing or beyond
      if (areAllItemsPreparing(updatedOrder) && updatedOrder.status === 'pending') {
        await orderService.updateOrder(order.id, { status: 'preparing' });
      }

      // Update the local order in the activeOrders array
      const orderIndex = activeOrders.value.findIndex(o => o.id === updatedOrder.id);
      if (orderIndex !== -1) {
        activeOrders.value[orderIndex] = initializeOrderItems(updatedOrder);
      }
    } catch (error) {
      console.error('Error updating item status:', error);
    }
  };

  /**
   * Mark individual item as ready
   */
  const markItemReady = async (order: Order, item: OrderItem) => {
    try {
      // Update the item status
      await orderService.updateOrderItemStatus(order.id, item.id, 'ready');

      // Fetch the updated order to get current state of all items
      const updatedOrder = await orderService.getOrder(order.id);

      // Check if all items in the order are now ready or delivered
      if (areAllItemsReady(updatedOrder) && updatedOrder.status !== 'ready') {
        await orderService.updateOrder(order.id, { status: 'ready' });
      }

      // Update the local order in the activeOrders array
      const orderIndex = activeOrders.value.findIndex(o => o.id === updatedOrder.id);
      if (orderIndex !== -1) {
        activeOrders.value[orderIndex] = initializeOrderItems(updatedOrder);
      }
    } catch (error) {
      console.error('Error updating item status:', error);
    }
  };

  /**
   * Start preparing all pending items in an order
   */
  const startPreparingAllItems = async (order: Order) => {
    try {
      const pendingItems = getKitchenVisibleItems(order).filter(
        item => item.status === 'pending'
      );

      if (pendingItems.length === 0) {
        return; // No items to update
      }

      // Update order status to preparing - backend will handle updating all pending items
      await orderService.updateOrder(order.id, { status: 'preparing' });

      // Fetch the updated order to get current state of all items
      const updatedOrder = await orderService.getOrder(order.id);

      // Update the local order in the activeOrders array
      const orderIndex = activeOrders.value.findIndex(o => o.id === updatedOrder.id);
      if (orderIndex !== -1) {
        activeOrders.value[orderIndex] = initializeOrderItems(updatedOrder);
      }
    } catch (error) {
      console.error('Error starting preparation:', error);
    }
  };

  /**
   * Mark all preparing items as ready
   */
  const markAllItemsReady = async (order: Order) => {
    try {
      const preparingItems = getKitchenVisibleItems(order).filter(
        item => item.status === 'preparing'
      );

      if (preparingItems.length === 0) {
        return; // No items to update
      }

      // Update order status to ready - backend will handle updating all preparing items
      await orderService.updateOrder(order.id, { status: 'ready' });

      // Fetch the updated order to get current state of all items
      const updatedOrder = await orderService.getOrder(order.id);

      // Update the local order in the activeOrders array
      const orderIndex = activeOrders.value.findIndex(o => o.id === updatedOrder.id);
      if (orderIndex !== -1) {
        activeOrders.value[orderIndex] = initializeOrderItems(updatedOrder);
      }
    } catch (error) {
      console.error('Error marking items ready:', error);
    }
  };

  return {
    // Methods
    markItemPreparing,
    markItemReady,
    startPreparingAllItems,
    markAllItemsReady
  };
}
